import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { neioState, TraceBody } from "../components/neio/TraceView";
import { Empty } from "../components/States";
import { fetchTrace, useNeioStatus, type NeioStatus, type TraceDetail } from "../lib/neio";

/** The trace screen's states and content, without the fetching. */
export function TraceScreenBody({ status, statusLoading, loading, error, detail, onRetry }: {
  status: NeioStatus | undefined;
  statusLoading: boolean;
  loading: boolean;
  error: unknown;
  detail: TraceDetail | undefined;
  onRetry?: () => void;
}) {
  const state = neioState({
    what: statusLoading ? "Checking NeIO Core" : "Reading the trace",
    loading: statusLoading || loading, status, error, onRetry,
    notFound: <Empty title="No trace at this address">NeIO Core holds no trace by this id. It may not have been sent yet.</Empty>,
  });
  if (state) return state;
  if (!detail) return <Empty title="No trace at this address" />;
  return <TraceBody detail={detail} />;
}

/** /traces/:requestId. One NeIO Core trace, for anyone scoped to its package. */
export function TracePage() {
  const { requestId = "" } = useParams();
  const { status, loading: statusLoading, error: statusError, refetch: refetchStatus } = useNeioStatus();
  const enabled = status?.enabled === true;
  const query = useQuery({
    queryKey: ["neio", "trace", requestId],
    queryFn: () => fetchTrace(requestId),
    enabled,
    retry: false,
  });
  // A disabled query reads as pending forever: only an enabled one is loading. A status that
  // could not be read is the error to show, and retrying it asks for the status again.
  const error = statusError ?? query.error;
  const onRetry = () => void (statusError ? refetchStatus() : query.refetch());
  return (
    <div className="page">
      <TraceScreenBody status={status} statusLoading={statusLoading && !statusError}
                       loading={enabled && query.isPending} error={error} detail={query.data} onRetry={onRetry} />
    </div>
  );
}
